import moment from 'moment'
import store from '@/store'

// In-flight plafond requests, keyed by driver id. Several components can ask
// for the same driver at once (the table view renders one cell per plafond
// column), and without this each would fire its own request: the cache in
// `loadDriverPlafondData` only helps once a response has come back.
const driverPlafondPending = new Map()

const actions = {
    // MAIN MAP
    async flyToMap({ state }, payload) {
        let map = state.mainMapInstance.mapObject

        if (map) {
            map.flyTo(payload.center, payload.zoom, { duration: 1 })
        }
    },
    async drawMarker({ commit }, payload) {
        let center = payload.center
        let title = payload.title
        let icon_unicode = payload.icon_unicode
        let ref = payload.ref
        let reference = payload.reference
        let customizer = payload.customizer
        let data = payload.data

        commit('addMainMapExtraObject', {
            object: {
                latLng: center,
                title,
                icon_unicode,
                ref,
                reference,
                customizer,
                data
            },
            type: 'markers'
        })
    },
    async drawCircle({ commit }, payload) {
        let center = payload.center
        let radius = payload.radius
        let ref = payload.ref

        commit('addMainMapExtraObject', {
            object: {
                latLng: center,
                radius: radius,
                ref: ref
            },
            type: 'circles'
        })
    },
    async removeMarker({ state, commit }, payload) {
        let ref = payload.ref

        let marker = state.mainMapExtraObjects.markers.find((m) => m.ref === ref)

        if (marker) {
            commit('removeMainMapExtraObject', { object: marker, type: 'markers' })
        }
    },
    async removeAllMarkers({ state, commit }) {
        let markers = state.mainMapExtraObjects.markers

        for (const marker of markers) {
            commit('removeMainMapExtraObject', { object: marker, type: 'markers' })
        }
    },
    async removeCircle({ state, commit }, payload) {
        let ref = payload.ref

        let circle = state.mainMapExtraObjects.circles.find((c) => c.ref === ref)

        if (circle) {
            commit('removeMainMapExtraObject', { object: circle, type: 'circles' })
        }
    },

    // CONFIG — Sprint 19 (REFACTOR-030): shim legacy `saveConfig`/`loadConfig`/
    // `resetConfig` RITIRATI. Tutti i consumer usano direttamente l'API del modulo
    // namespaced `config` (load / commitChange / reset / flush) — vedi
    // docs/architecture/config-bus.md §5. NB per i merge da branch pre-Bus: i
    // pattern `dispatch('saveConfig', ...)` in arrivo NON hanno più copertura
    // runtime e vanno convertiti in fase di merge.

    // TRUCKS
    async loadTrucks({ commit }, payload) {
        let vm = payload.vueInstance

        let trucks = await vm
            .$fetch(
                vm.$env.GOLIA_API +
                    '/trucks?include[]=lastpoint&include[]=details&include[]=category&include[]=tags&filter[VISIBILE][eq]=1'
            )
            .then((res) => {
                return res.json()
            })

        commit('setTrucks', trucks.data)
    },
    async loadTrucksEvents({ commit }, payload) {
        let vm = payload.vueInstance
        let params = {}

        if (payload.update) {
            params.update = payload.update
        }

        let query_string = new URLSearchParams(params).toString()

        // let trucksEvents = await vm.$fetch(vm.$env.GOLIA_API + '/trucks/events').then((res) => {
        let trucksEvents = await vm
            .$fetch(
                'https://run.mocky.io/v3/2de7f863-2d37-4225-8303-7af83ca7fd48' + '?' + query_string
            )
            .then((res) => {
                return res.json()
            })

        commit('setTrucksEvents', trucksEvents)

        // const lastEvent = trucksEvents[trucksEvents.length - 1]
        // commit('setTrucksLastEventId', lastEvent.id)
    },

    // COMPANIES
    async loadCompanies({ commit }, payload) {
        let vm = payload.vueInstance

        let companies_obj = {}

        let companies = await vm.$fetch(vm.$env.GOLIA_API + '/companies').then((res) => {
            return res.json()
        })

        companies_obj[companies.data.company.id] = companies.data.company.business_name

        for (var company of companies.data.master_slave) {
            companies_obj[company.id] = company.business_name
        }

        commit('setCompanies', companies.data)
    },

    // COMPANY TAGS
    async loadCompanyTags({ commit }, vueInstance) {
        let tags = await vueInstance.$fetch(vueInstance.$env.GOLIA_API + '/tags').then((res) => {
            return res.json()
        })

        tags.data.forEach((tag) => {
            tag.loading = false
            tag.error = false
            tag.to_update = false
        })

        commit('setCompanyTags', tags.data)
    },
    async updateCompanyTag({ commit }, payload) {
        let vm = payload.vueInstance
        let tag = payload.item

        let update = await vm
            .$fetch(vm.$env.GOLIA_API + '/tags/' + tag.id, {
                method: 'PUT',
                body: JSON.stringify({ tag: tag.name })
            })
            .then((res) => {
                return res.json()
            })
            .catch((err) => {
                vm.$snotify.error(err, { position: 'centerTop' })
            })

        if (!update.errors) {
            tag.loading = false
            tag.error = false
            tag.to_update = false

            vm.$snotify.success(vm.__('tags.update_tag_success'), { position: 'centerTop' })
        } else {
            tag.error = true
            tag.loading = false
            tag.to_update = false

            vm.$snotify.error(vm.__('tags.update_tag_error'), { position: 'centerTop' })
        }

        commit('updateCompanyTag', tag)
    },
    async addCompanyTag({ commit, dispatch }, payload) {
        let vm = payload.vueInstance
        let tag = payload.item

        let insert = await payload.vueInstance
            .$fetch(payload.vueInstance.$env.GOLIA_API + '/tags', {
                method: 'POST',
                body: JSON.stringify({ tag: tag.name })
            })
            .then((res) => {
                return res.json()
            })
            .catch((err) => {
                vm.$snotify.error(err, { position: 'centerTop' })
            })

        if (!insert.errors) {
            tag.loading = false
            tag.error = false
            tag.to_update = false

            dispatch('loadCompanyTags', vm)
            vm.$snotify.success(vm.__('tags.add_tag_success'), { position: 'centerTop' })
        } else {
            tag.error = true
            tag.loading = false

            vm.$snotify.error(vm.__('tags.add_tag_error'), { position: 'centerTop' })
        }

        commit('addCompanyTag', tag)
    },
    async removeCompanyTag({ commit }, payload) {
        let vm = payload.vueInstance
        let tag = payload.item

        let remove = await vm.$fetch(vm.$env.GOLIA_API + '/tags/' + tag.id, {
            method: 'DELETE'
        })

        if (!remove.errors) {
            tag.loading = false
            tag.error = false

            vm.$snotify.success(vm.__('tags.delete_tag_success'), { position: 'centerTop' })
        } else {
            tag.error = true
            tag.loading = false

            vm.$snotify.error(vm.__('tags.delete_tag_error'), { position: 'centerTop' })
        }

        commit('removeCompanyTag', tag)
    },

    // DRIVERS DATA
    async loadDriversData({ commit }, payload) {
        let vm = payload.vueInstance
        let params = { driving: true }

        if (payload.update) {
            params.update = payload.update
        }

        let query_string = new URLSearchParams(params).toString()

        let drivers = await vm
            .$fetch(vm.$env.GOLIA_API + '/drivers/plafond?' + query_string)
            .then((res) => {
                return res.json()
            })

        if (payload.update) {
            commit('updateDriversData', drivers.data)
        } else {
            commit('setDriversData', drivers.data)
        }
    },
    async loadDrivers({ commit }, payload) {
        let vm = payload.vueInstance

        let drivers = await vm.$fetch(vm.$env.GOLIA_API + '/geofleet/drivers').then((res) => {
            return res.json()
        })

        commit('setDrivers', drivers.data)
    },
    async loadDriverPlafondData({ state, commit }, payload) {
        const vm = payload.vueInstance
        const driverId = payload.driverId

        const cachedPlafondDriver = state.driversPlafondData?.[driverId]
        if (cachedPlafondDriver && moment().diff(cachedPlafondDriver.__cacheTime, 'minutes') < 1) {
            return cachedPlafondDriver
        }

        if (driverPlafondPending.has(driverId)) return driverPlafondPending.get(driverId)

        const translation_language = store.state.loggedUser?.params?.Lingua || 'it-IT'
        const infringement = store.state.loggedUser?.params?.infringment_nation || 'ITA'

        const request = vm
            .$fetch(
                `${vm.$env.GOLIA_API}/drivers/${driverId}/plafond?translation_language=${translation_language}&infringement_nation=${infringement}&timezone=UTC`
            )
            .then((res) => {
                return res.json()
            })
            .then((driverPlafond) => {
                commit('setDriversPlafondData', { id: driverId, data: driverPlafond })

                return driverPlafond
            })
            .finally(() => driverPlafondPending.delete(driverId))

        driverPlafondPending.set(driverId, request)

        return request
    },

    // BLUETOOTH DEVICES
    async loadBluetoothDevices({ commit }, payload) {
        let vm = payload.vueInstance

        let devices = await vm
            // .$fetch('https://run.mocky.io/v3/3021ee38-4b34-4af1-9fbb-6a2e18d238ac')
            .$fetch(vm.$env.GOLIA_API + '/geofleet/bluetooth_accessories')
            .then((res) => {
                return res.json()
            })

        commit('setBluetoothDevices', devices.data)
    }
}

export default actions
